<template>
  <div class="my-devices">
    <!-- 查询区域 -->
    <div class="query-section">
      <el-form :model="searchForm" inline class="query-form">
        <el-form-item label="账号：">
          <el-input
            v-model="searchForm.username"
            placeholder="请输入账号"
            clearable
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item class="button-group">
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 数据展示区域 -->
    <div class="data-section">
      <!-- 标题和工具栏 -->
      <div class="section-header">
        <div class="header-title">
          <span style="font-size: 14px; color: #333">操作日志</span>
        </div>
        <div class="toolbar">
          <!-- 刷新按钮 -->
          <el-button type="text" @click="refreshData">
            <el-icon><Refresh /></el-icon>
          </el-button>

          <!-- 密度设置 -->
          <el-popover
            placement="bottom"
            width="160"
            v-model:visible="densityDialogVisible"
          >
            <div class="popover-content">
              <el-button
                class="density-button"
                :type="rowHeight === '80px' ? 'primary' : ''"
                @click="setRowHeight('80px')"
              >
                默认
              </el-button>
              <el-button
                class="density-button"
                :type="rowHeight === '60px' ? 'primary' : ''"
                @click="setRowHeight('60px')"
              >
                中等
              </el-button>
              <el-button
                class="density-button"
                :type="rowHeight === '30px' ? 'primary' : ''"
                @click="setRowHeight('30px')"
              >
                紧凑
              </el-button>
            </div>
            <template #reference>
              <el-button type="text">
                <el-icon><Grid /></el-icon>
              </el-button>
            </template>
          </el-popover>

          <!-- 列显示设置 -->
          <el-popover
            placement="bottom"
            width="100"
            v-model:visible="columnsDialogVisible"
          >
            <div class="popover-content">
              <el-checkbox-group v-model="tempSelectedColumns">
                <el-checkbox
                  v-for="column in columns"
                  :key="column.prop"
                  :label="column.prop"
                >
                  {{ column.label }}
                </el-checkbox>
              </el-checkbox-group>
              <div class="popover-footer">
                <el-button @click="resetColumns">重置</el-button>
                <el-button type="primary" @click="confirmColumns">确定</el-button>
              </div>
            </div>
            <template #reference>
              <el-button type="text">
                <el-icon><Setting /></el-icon>
              </el-button>
            </template>
          </el-popover>
        </div>
      </div>

      <!-- 表格区域 -->
      <el-table
        v-loading="loading"
        :data="tableData"
        style="width: 98.5%; margin-left: 10px; margin-right: 20px"
        :row-style="getRowStyle"
        :row-class-name="tableRowClassName"
        :header-cell-style="{
          background: '#f5f7fa',
          color: '#909399',
          textAlign: 'center',
        }"
      >
        <el-table-column
          v-for="column in visibleColumns"
          :key="column.prop"
          :prop="column.prop"
          :label="column.label"
          :width="column.width"
          :align="column.align || 'center'"
        />

        <template #empty>
          <div class="empty-state">
            <el-icon class="empty-icon"><Box /></el-icon>
            <p class="empty-text">暂无数据</p>
          </div>
        </template>
      </el-table>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { ElMessage } from "element-plus";
import { Box, Refresh, Grid, Setting, Loading } from "@element-plus/icons-vue";
import { getAllLogs, getLogsByUsername } from "@/api/operation";

export default {
  name: "OperationLogPage",
  components: {
    Box,
    Refresh,
    Grid,
    Setting,
  },
  setup() {
    const tableData = ref([]);
    const searchForm = ref({
      username: "",
    });

    // 表格配置
    const columns = ref([
      { prop: "operationTime", label: "操作时间", visible: true },
      { prop: "username", label: "账号", visible: true },
      { prop: "role", label: "角色", visible: true },
      { prop: "operation", label: "操作", visible: true },
      { prop: "ipAddress", label: "IP地址", visible: true },
    ]);

    const visibleColumns = computed(() =>
      columns.value.filter((col) => selectedColumns.value.includes(col.prop))
    );

    const selectedColumns = ref(columns.value.map((col) => col.prop));
    const tempSelectedColumns = ref([]);
    const rowHeight = ref("80px");
    const densityDialogVisible = ref(false);
    const columnsDialogVisible = ref(false);

    const loading = ref(false);

    // 加载数据
    const loadData = async (username = "", isShow=true) => {
      loading.value = true;
      try {
        const response = username
          ? await getLogsByUsername(username)
          : await getAllLogs();
        
        if (response.success) {
          const logs = response.data;
          console.log("logs:", logs)
          tableData.value = logs.map(log => ({
            operationTime: new Date(log.operationTime).toLocaleString('zh-CN', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: false
            }).replace(/\//g, '-'),
            username: log.operatorUsername,
            role: getRoleName(log.operatorRole),
            operation: `${log.operationType} ${log.targetType}`,
            ipAddress: log.ipAddress || '-'
          }));
          if(isShow)
            ElMessage.success("数据已刷新");
        } else {
          ElMessage.error("获取日志数据失败");
        }
      } catch (error) {
        console.error("加载日志数据出错:", error);
        ElMessage.error("加载日志数据失败");
      } finally {
        loading.value = false;
      }
    };

    // 角色名称转换
    const getRoleName = (roleId) => {
      const roleMap = {
        1: "员工",
        2: "维修工程师",
        0: "管理员"
      };
      return roleMap[roleId] || "未知角色";
    };

    // 查询
    const handleSearch = () => {
      loadData(searchForm.value.username, true);
    };

    // 重置
    const handleReset = () => {
      searchForm.value.username = "";
      loadData("", true);
    };

    // 表格样式
    const getRowStyle = () => {
      return { height: rowHeight.value };
    };

    const tableRowClassName = ({ rowIndex }) =>
      rowIndex % 2 === 0 ? "even-row" : "odd-row";

    // 刷新数据
    const refreshData = () => {
      loadData(searchForm.value.username? searchForm.value.username : "", true);
    };

    // 设置表格行高
    const setRowHeight = (height) => {
      rowHeight.value = height;
      densityDialogVisible.value = false;
    };

    // 重置列显示
    const resetColumns = () => {
      tempSelectedColumns.value = columns.value.map((col) => col.prop);
      selectedColumns.value = [...tempSelectedColumns.value];
      columnsDialogVisible.value = false;
    };

    // 确认列显示设置
    const confirmColumns = () => {
      if (tempSelectedColumns.value.length === 0) {
        ElMessage.warning("至少选择一列");
        return;
      }
      selectedColumns.value = [...tempSelectedColumns.value];
      columnsDialogVisible.value = false;
    };

    onMounted(() => {
      loadData("", false);
      tempSelectedColumns.value = [...selectedColumns.value];
    });

    return {
      tableData,
      searchForm,
      columns,
      visibleColumns,
      selectedColumns,
      tempSelectedColumns,
      rowHeight,
      densityDialogVisible,
      columnsDialogVisible,
      getRowStyle,
      tableRowClassName,
      handleSearch,
      handleReset,
      refreshData,
      setRowHeight,
      resetColumns,
      confirmColumns,
      Loading
    };
  },
};
</script>

<style scoped>
.my-devices {
  background-color: #f5f7fa;
  min-height: 100vh;
}

.query-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 10px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.query-form {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.query-form :deep(.el-form-item) {
  margin-bottom: 0;
}

.button-group {
  margin-left: auto;
}

.data-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0px;
}

.search-section {
  margin: 20px 0;
  padding: 0;
}

.toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 0px;
}

.even-row {
  background-color: #fafafa;
}

.odd-row {
  background-color: #ffffff;
}

.empty-state {
  padding: 40px 0;
  text-align: center;
}

.empty-icon {
  font-size: 64px;
  color: #909399;
  margin-bottom: 16px;
}

.empty-text {
  color: #909399;
  margin: 0;
  font-size: 14px;
}

.popover-content {
  display: flex;
  flex-direction: column;
}

.popover-footer {
  display: flex;
  justify-content: center;
  margin-top: auto;
  padding: 10px;
}

.density-button {
  display: flex;
  justify-content: center;
  margin: 10px;
}
</style>
